import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Calendar } from '@/components/ui/calendar';
import { CalendarIcon, Zap, Loader2, Link as LinkIcon } from 'lucide-react';
import { format } from 'date-fns';
import { platformLabel } from '@/lib/clipPlatformFormats';

/**
 * Schedule → Scheduling (section 6): one-off scheduling for a single
 * (variant, platform) pair. Publish Now calls the real
 * `/api/platforms/post-now` route (see `lib/scheduleData.js`) — if the
 * creator has no active connection for this platform, it's disabled
 * with a link to Connect rather than faking a result.
 */
export function ScheduleOneOffPanel({ item, date, time, onDateChange, onTimeChange, onConfirm, onPublishNow, publishing, hasConnection, publishAssetAvailable, disabled }) {
  const [open, setOpen] = useState(false);
  if (disabled || !item) return <p className="text-xs text-[#8A93A6] text-center py-6" data-testid="schedule-oneoff-empty">Pick an item from the Content Queue, or click a day on the calendar.</p>;

  return (
    <div className="space-y-3" data-testid="schedule-oneoff-panel">
      <div className="p-2.5 rounded-lg border border-white/10 bg-white/[0.02]">
        <p className="text-[10px] text-[#555E6E]">Scheduling</p>
        <p className="text-sm text-white truncate">{item.variant.contentTitle || item.variant.title}</p>
        <p className="text-[11px] text-[#8A93A6]">{platformLabel(item.platform)}</p>
      </div>

      <div>
        <Label className="text-[11px] text-[#8A93A6]">Date</Label>
        <Popover open={open} onOpenChange={setOpen}>
          <PopoverTrigger asChild>
            <Button variant="outline" className="w-full justify-start border-white/10 mt-1 text-xs" data-testid="schedule-oneoff-date-trigger"><CalendarIcon className="h-3.5 w-3.5 mr-2" />{date ? format(date, 'PPP') : 'Pick a date'}</Button>
          </PopoverTrigger>
          <PopoverContent className="p-0 w-auto"><Calendar mode="single" selected={date} onSelect={(d) => { onDateChange(d); setOpen(false); }} /></PopoverContent>
        </Popover>
      </div>
      <div>
        <Label className="text-[11px] text-[#8A93A6]">Time</Label>
        <Input type="time" value={time} onChange={(e) => onTimeChange(e.target.value)} className="glass mt-1 text-xs" data-testid="schedule-oneoff-time-input" />
      </div>

      <Button className="w-full bg-[#3A7BFF] hover:bg-[#3A7BFF]/90" onClick={onConfirm} disabled={!date || !time} data-testid="schedule-oneoff-confirm-button">Confirm Scheduling</Button>

      <div className="pt-2 border-t border-white/5">
        {!hasConnection ? (
          <a href="/app/connect" className="flex items-center justify-center gap-1.5 text-xs text-[#8A93A6] hover:text-white border border-dashed border-white/15 rounded-lg py-2" data-testid="schedule-oneoff-connect-link">
            <LinkIcon className="h-3 w-3" />Connect {platformLabel(item.platform)} to enable Publish Now
          </a>
        ) : !publishAssetAvailable ? (
          <div className="flex items-center justify-center gap-1.5 text-xs text-[#555E6E] border border-dashed border-white/10 rounded-lg py-2 px-2 text-center" data-testid="schedule-oneoff-publish-unavailable">
            <LinkIcon className="h-3 w-3 shrink-0" />Publish Now isn't available yet for this content type
          </div>
        ) : (
          <Button variant="outline" className="w-full border-amber-500/30 text-amber-300" onClick={onPublishNow} disabled={publishing} data-testid="schedule-oneoff-publish-now-button">
            {publishing ? <Loader2 className="h-3.5 w-3.5 mr-2 animate-spin" /> : <Zap className="h-3.5 w-3.5 mr-2" />}Publish Now
          </Button>
        )}
        <p className="text-[10px] text-[#555E6E] mt-1.5">Publish Now calls the real posting pipeline, which runs in safe mock mode by default — nothing is marked Published unless that call actually succeeds.</p>
      </div>
    </div>
  );
}
